/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, type HTMLAttributes, createContext, forwardRef, isValidElement } from 'react';
import { useFocusVisible, useHover } from 'react-aria';
import { type ContextValue, DEFAULT_SLOT, FileTrigger as FileTriggerCore, Provider, useContextProps } from 'react-aria-components';
import { AnonymousIncognitoFillIcon } from '../../icons/anonymous-incognito-fill/anonymous-incognito-fill.icon';
import { EditStrokeIcon } from '../../icons/edit-stroke/edit-stroke.icon';
import { UserFillIcon } from '../../icons/user-fill/user-fill.icon';
import { UsersGroupV2FillIcon } from '../../icons/users-group-v-2-fill/users-group-v-2-fill.icon';
import { $ } from '../../tokens.stylex';
import type { AvatarStatusProps } from '../avatar-status/avatar-status.component';
import { AvatarStatus, AvatarStatusContext } from '../avatar-status/avatar-status.component';
import { IconButton, IconButtonContext } from '../icon-button/icon-button.component';
import type { IconProps } from '../icon/icon.component';
import { IconContext } from '../icon/icon.component';
import { ImageContext } from '../image/image.component';
import type { TextProps } from '../text/text.component';
import { Text, TextContext } from '../text/text.component';

interface AvatarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className' | 'onChange'> {
  /** The function to call when the user selects an image. */
  onChange?: (file: File | null | undefined) => void;
  /** The size of the avatar. (Default: 40) */
  size?: keyof typeof sizeVariants;
  /** Whether the user is incognito. (Default: false) */
  isIncognito?: boolean;
  /** Whether the user is an organization. (Default: false) */
  isOrganization?: boolean;
  /** Whether the user is online. (Default: undefined) */
  isOnline?: boolean;
  style?: stylex.StyleXStyles;
}

const AvatarContext = createContext<ContextValue<AvatarProps, HTMLDivElement>>({});

const Avatar = forwardRef((props: AvatarProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, AvatarContext);
  const { hoverProps, isHovered } = useHover({});
  const { isFocusVisible } = useFocusVisible({});

  const { style, size = 40, isOrganization = false, isIncognito, isOnline, onChange, children, ...coreProps } = props;

  const childrenArray = Children.toArray(children);

  const isTextOnly = childrenArray.every(c => !isValidElement(c));

  const shouldShowPlaceholder = !children;
  const shouldShowStatus = isOnline !== undefined && 20 < size && size < 64;
  const shouldShowEditButton = onChange && isHovered && 64 <= size;

  const shouldHaveGrayBackground = !isIncognito && !shouldShowPlaceholder && (
    (childrenArray.length === 1 && isTextOnly) ||
    childrenArray.every(c => isValidElement(c) && typeof c.props === 'object' && c.props !== null && 'children' in c.props)
  );

  return (
    <div
      ref={ref}
      data-focus-visible={isFocusVisible || undefined}
      data-hovered={isHovered || undefined}
      {...stylex.props(styles.base, isOrganization && styles.organization, style)}
      {...coreProps}
      {...hoverProps}>
      <Provider values={[
        [TextContext, {
          slots: {
            [DEFAULT_SLOT]: {
              size: TWO_LETTER_INITIALS_TEXT_SIZE[size],
              weight: TEXT_WEIGHT[size],
              color: 'quaternary',
              style: textStyles.base
            },
            [AVATAR_SLOTS.TWO_LETTER_INITIALS_TEXT]: {
              size: TWO_LETTER_INITIALS_TEXT_SIZE[size],
              weight: TEXT_WEIGHT[size],
              color: 'quaternary',
              style: textStyles.base
            },
            [AVATAR_SLOTS.ONE_LETTER_INITIALS_TEXT]: {
              size: ONE_LETTER_INITIALS_TEXT_SIZE[size],
              weight: TEXT_WEIGHT[size],
              color: 'quaternary',
              style: textStyles.base
            },
          }
        }],
        [IconContext, {
          slots: {
            [DEFAULT_SLOT]: {
              color: 'disabled',
              size: ICON_SIZE[size],
            },
            [INTERNAL_AVATAR_SLOTS.INCOGNITO]: {
              color: 'disabled',
              size: ICON_SIZE[size],
              style: placeholderIconStyles.base
            },
            [INTERNAL_AVATAR_SLOTS.PLACEHOLDER]: {
              color: 'disabled',
              size,
              style: placeholderIconStyles.base
            },
          }
        }],
        [AvatarStatusContext, {
          size: AVATAR_STATUS_SIZE[size],
          style: [avatarStatusStyles.base, avatarStatusSizeVariants[size]]
        }],
        [ImageContext, {
          src: '',
          style: imageStyles.base,
        }],
        [IconButtonContext, {
          variant: 'alternative',
          isRounded: true,
          size: 52,
          style: [iconButtonStyles.base, !shouldShowEditButton && iconButtonStyles.hidden]
        }]
      ]}>
        <div
          {...stylex.props(
            contentStyles.base,
            sizeVariants[size],
            isOrganization && contentStyles.organization,
            shouldHaveGrayBackground && contentStyles.gray,
          )}>
          {isIncognito
            ? <AnonymousIncognitoFillIcon />
            : shouldShowPlaceholder
              ? isOrganization
                ? <UsersGroupV2FillIcon slot={INTERNAL_AVATAR_SLOTS.PLACEHOLDER} />
                : <UserFillIcon slot={INTERNAL_AVATAR_SLOTS.PLACEHOLDER} />
              : isTextOnly ? <Text>{children}</Text> : children}

          <FileTriggerCore acceptedFileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']} onSelect={(files) => onChange?.(files?.item(0))}>
            <IconButton aria-label="Change avatar">
              <EditStrokeIcon />
            </IconButton>
          </FileTriggerCore>
        </div>
        {shouldShowStatus && <AvatarStatus isOnline={isOnline} />}
      </Provider>
    </div>
  );
});

const TWO_LETTER_INITIALS_TEXT_SIZE: Record<keyof typeof sizeVariants, TextProps['size']> = {
  8: undefined,
  12: undefined,
  16: undefined,
  20: 10,
  24: 10,
  28: 12,
  32: 14,
  40: 14,
  48: 16,
  56: 16,
  64: 18,
  72: 18,
  80: 20,
  100: 20,
  104: 20,
};

const ONE_LETTER_INITIALS_TEXT_SIZE: Record<keyof typeof sizeVariants, TextProps['size']> = {
  8: undefined,
  12: undefined,
  16: undefined,
  20: 12,
  24: 12,
  28: 14,
  32: 16,
  40: 16,
  48: 18,
  56: 18,
  64: 18,
  72: 18,
  80: 20,
  100: 20,
  104: 20,
};

const ICON_SIZE: Record<keyof typeof sizeVariants, IconProps['size']> = {
  8: 6,
  12: 9,
  16: 12,
  20: 14,
  24: 18,
  28: 22,
  32: 24,
  40: 28,
  48: 34,
  56: 40,
  64: 46,
  72: 52,
  80: 56,
  100: 56,
  104: 56,
};

const AVATAR_STATUS_SIZE: Record<keyof typeof sizeVariants, AvatarStatusProps['size']> = {
  8: 10,
  12: 10,
  16: 10,
  20: 10,
  24: 10,
  28: 10,
  32: 11,
  40: 12,
  48: 12,
  56: 14,
  64: 14,
  72: 14,
  80: 14,
  100: 14,
  104: 14,
};

const TEXT_WEIGHT: Record<keyof typeof sizeVariants, TextProps['weight']> = {
  8: undefined,
  12: undefined,
  16: undefined,
  20: 'semibold',
  24: 'semibold',
  28: 'semibold',
  32: 'semibold',
  40: 'semibold',
  48: 'medium',
  56: 'medium',
  64: 'medium',
  72: 'medium',
  80: 'medium',
  100: 'medium',
  104: 'medium',
};

const styles = stylex.create({
  base: {
    borderRadius: 999,
    display: 'inline-flex',
    outlineColor: {
      default: 'transparent',
      ':is([data-focus-visible])': $.borderInteractive,
    },
    position: 'relative',
  },
  organization: {
    borderRadius: '25%',
  },
});

const contentStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: $.surface02,
    borderRadius: 999,
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  organization: {
    borderRadius: '25%',
  },
  gray: {
    backgroundColor: $.surface03,
  }
});

const sizeVariants = stylex.create({
  8: {
    height: 8,
    width: 8,
  },
  12: {
    height: 12,
    width: 12,
  },
  16: {
    height: 16,
    width: 16,
  },
  20: {
    height: 20,
    width: 20,
  },
  24: {
    height: 24,
    width: 24,
  },
  28: {
    height: 28,
    width: 28,
  },
  32: {
    height: 32,
    width: 32,
  },
  40: {
    height: 40,
    width: 40,
  },
  48: {
    height: 48,
    width: 48,
  },
  56: {
    height: 56,
    width: 56,
  },
  64: {
    height: 64,
    width: 64,
  },
  72: {
    height: 72,
    width: 72,
  },
  80: {
    height: 80,
    width: 80,
  },
  100: {
    height: 100,
    width: 100,
  },
  104: {
    height: 104,
    width: 104,
  },
});

const placeholderIconStyles = stylex.create({
  base: {
    bottom: 0,
    position: 'absolute',
    transform: 'translateY(20%)',
  }
});

const avatarStatusStyles = stylex.create({
  base: {
    bottom: 0,
    position: 'absolute',
    right: 0,
  }
});

const avatarStatusSizeVariants = stylex.create({
  8: {},
  12: {},
  16: {},
  20: {},
  24: {
    height: 10,
    width: 10,
  },
  28: {
    height: 10,
    width: 10,
  },
  32: {
    height: 11,
    width: 11,
  },
  40: {
    height: 12,
    transform: 'translate(-10%, -10%)',
    width: 12,
  },
  48: {
    height: 12,
    transform: 'translate(-15%, -15%)',
    width: 12,
  },
  56: {
    height: 14,
    transform: 'translate(-15%, -15%)',
    width: 14,
  },
  64: {},
  72: {},
  80: {},
  100: {},
  104: {},
});

const imageStyles = stylex.create({
  base: {
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  }
});

const iconButtonStyles = stylex.create({
  base: { position: 'absolute' },
  hidden: { display: 'none' }
});

const textStyles = stylex.create({
  base: {
    textTransform: 'uppercase',
  }
});

const INTERNAL_AVATAR_SLOTS = {
  PLACEHOLDER: 'placeholder',
  INCOGNITO: 'incognito',
};

const AVATAR_SLOTS = {
  TWO_LETTER_INITIALS_TEXT: 'twoLetterInitialsText',
  ONE_LETTER_INITIALS_TEXT: 'oneLetterInitialsText',
};

export { AVATAR_SLOTS, Avatar, AvatarContext, type AvatarProps };

